<template>
  <div>
    <v-row dense class="mb-4">
      <v-col cols="12" sm="4" md="2">
        <v-card outlined class="stat-card" @click="openDialog('total')">
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold primary--text">
              {{ stats ? stats.total_tasks : 0 }}
            </div>
            <div class="caption">Total Tasks</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4" md="2">
        <v-card outlined class="stat-card" @click="openDialog('completed')">
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold green--text">
              {{ stats ? stats.completed : 0 }}
            </div>
            <div class="caption">Completed</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4" md="2">
        <v-card outlined class="stat-card" @click="openDialog('missed')">
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold red--text">
              {{ stats ? stats.missed : 0 }}
            </div>
            <div class="caption">Missed</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4" md="2">
        <v-card outlined class="stat-card" @click="openDialog('overdue')">
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold orange--text">
              {{ stats ? stats.overdue : 0 }}
            </div>
            <div class="caption">Overdue</div>
            <div
              v-if="stats && stats.ongoing > 0"
              class="caption orange--text mt-1"
            >
              {{ stats.ongoing }} ongoing
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4" md="2">
        <v-card outlined class="stat-card" @click="openDialog('pending')">
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold blue--text">
              {{ stats ? stats.pending_acknowledgment : 0 }}
            </div>
            <div class="caption">Pending Review</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4" md="2">
        <v-card outlined class="stat-card" @click="openDialog('rate')">
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold">
              {{ stats ? stats.completion_rate : 0 }}%
            </div>
            <div class="caption">Completion Rate</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Summary Dialog -->
    <v-dialog v-model="showDialog" max-width="700" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <template v-if="selectedUser">
            <v-btn icon class="mr-1" @click="selectedUser = null">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon left :color="dialogMeta.color">{{
              dialogMeta.icon
            }}</v-icon>
            <div>
              {{ selectedUser.user_name }}'s Tasks
              <div class="subtitle-2 grey--text font-weight-regular">
                {{ dateLabel }}
              </div>
            </div>
          </template>
          <template v-else>
            <v-icon left :color="dialogMeta.color">{{
              dialogMeta.icon
            }}</v-icon>
            <div>
              {{ dialogMeta.title }}
              <div class="subtitle-2 grey--text font-weight-regular">
                {{ dateLabel }}
              </div>
            </div>
          </template>
          <v-spacer />
          <v-btn icon @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <!-- Per-user summary view -->
          <template v-if="!selectedUser">
            <div class="d-flex align-center px-4 pt-3">
              <v-btn-toggle
                v-model="viewType"
                mandatory
                dense
                color="primary"
                @change="onViewChange"
              >
                <v-btn small value="daily">Daily</v-btn>
                <v-btn small value="weekly">Weekly</v-btn>
              </v-btn-toggle>
            </div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>User</th>
                  <th
                    class="text-center"
                    :class="{ 'highlight-col': activeMetric === 'total' }"
                  >
                    Total
                  </th>
                  <th
                    class="text-center"
                    :class="{ 'highlight-col': activeMetric === 'completed' }"
                  >
                    Completed
                  </th>
                  <th
                    class="text-center"
                    :class="{
                      'highlight-col':
                        activeMetric === 'missed' || activeMetric === 'overdue',
                    }"
                  >
                    Missed
                  </th>
                  <th
                    class="text-center"
                    :class="{ 'highlight-col': activeMetric === 'rate' }"
                  >
                    Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="perUserStats.length === 0">
                  <td colspan="5" class="text-center grey--text pa-4">
                    No data available
                  </td>
                </tr>
                <tr
                  v-for="u in sortedUserStats"
                  :key="u.user_id"
                  class="user-row"
                  @click="selectUser(u)"
                >
                  <td>
                    {{ u.user_name }}
                    <v-icon x-small class="ml-1 grey--text text--lighten-1"
                      >mdi-chevron-right</v-icon
                    >
                  </td>
                  <td
                    class="text-center"
                    :class="{
                      'highlight-col font-weight-bold primary--text':
                        activeMetric === 'total',
                    }"
                  >
                    {{ u.total }}
                  </td>
                  <td
                    class="text-center"
                    :class="{
                      'highlight-col font-weight-bold green--text':
                        activeMetric === 'completed',
                    }"
                  >
                    {{ u.completed }}
                  </td>
                  <td
                    class="text-center"
                    :class="{
                      'highlight-col font-weight-bold red--text':
                        activeMetric === 'missed' || activeMetric === 'overdue',
                    }"
                  >
                    {{ u.missed }}
                  </td>
                  <td
                    class="text-center"
                    :class="{
                      'highlight-col font-weight-bold': activeMetric === 'rate',
                    }"
                  >
                    {{ u.completion_rate }}%
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="perUserStats.length > 0">
                <tr class="font-weight-bold" style="background: #f5f5f5">
                  <td>Totals</td>
                  <td class="text-center">{{ totals.total }}</td>
                  <td class="text-center green--text">
                    {{ totals.completed }}
                  </td>
                  <td class="text-center red--text">{{ totals.missed }}</td>
                  <td class="text-center">{{ totals.rate }}%</td>
                </tr>
              </tfoot>
            </v-simple-table>
          </template>

          <!-- User task detail view -->
          <template v-else>
            <div v-if="loadingUserTasks" class="text-center pa-6">
              <v-progress-circular indeterminate color="primary" />
              <div class="caption mt-2">Loading tasks...</div>
            </div>
            <div
              v-else-if="userTaskLogs.length === 0"
              class="text-center grey--text pa-6"
            >
              No task logs found for this period.
            </div>
            <v-simple-table v-else dense>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th class="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in userTaskLogs" :key="log.id">
                  <td>
                    <div class="font-weight-medium">
                      {{ log.task ? log.task.title : "—" }}
                    </div>
                    <div
                      v-if="log.task && log.task.description"
                      class="caption grey--text text-truncate"
                      style="max-width: 280px"
                    >
                      {{ log.task.description }}
                    </div>
                  </td>
                  <td>
                    <v-chip
                      v-if="log.task && log.task.category"
                      x-small
                      outlined
                    >
                      {{ log.task.category }}
                    </v-chip>
                    <span v-else class="grey--text">—</span>
                  </td>
                  <td class="caption">{{ formatDate(taskDate(log)) }}</td>
                  <td class="text-center">
                    <v-chip
                      x-small
                      :color="statusColor(log.status)"
                      :text-color="log.status === 'pending' ? '' : 'white'"
                      :outlined="log.status === 'pending'"
                    >
                      {{ log.status }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn v-if="selectedUser" text @click="selectedUser = null">
            <v-icon left small>mdi-arrow-left</v-icon> Back
          </v-btn>
          <v-spacer />
          <v-btn text @click="showDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

const METRIC_CONFIG = {
  total: {
    title: "Total Tasks - Per User Breakdown",
    icon: "mdi-clipboard-list-outline",
    color: "primary",
    sortKey: "total",
  },
  completed: {
    title: "Completed Tasks - Per User Breakdown",
    icon: "mdi-check-circle-outline",
    color: "green",
    sortKey: "completed",
  },
  missed: {
    title: "Missed Tasks - Per User Breakdown",
    icon: "mdi-close-circle-outline",
    color: "red",
    sortKey: "missed",
  },
  overdue: {
    title: "Overdue Tasks - Per User Breakdown",
    icon: "mdi-alert-outline",
    color: "orange",
    sortKey: "missed",
  },
  pending: {
    title: "Pending Review - Per User Breakdown",
    icon: "mdi-clock-outline",
    color: "blue",
    sortKey: "total",
  },
  rate: {
    title: "Completion Rate - Per User Breakdown",
    icon: "mdi-percent-outline",
    color: "grey darken-1",
    sortKey: "completion_rate",
  },
};

export default {
  props: {
    stats: { type: Object, default: null },
    dateFrom: { type: String, default: "" },
    dateTo: { type: String, default: "" },
  },
  data() {
    return {
      showDialog: false,
      activeMetric: "total",
      viewType: "daily",
      selectedUser: null,
      userTaskLogs: [],
      loadingUserTasks: false,
    };
  },
  computed: {
    perUserStats() {
      return this.stats && this.stats.per_user_stats
        ? this.stats.per_user_stats
        : [];
    },
    dialogMeta() {
      return METRIC_CONFIG[this.activeMetric] || METRIC_CONFIG.total;
    },
    sortedUserStats() {
      const key = this.dialogMeta.sortKey;
      return [...this.perUserStats].sort(
        (a, b) => (b[key] || 0) - (a[key] || 0)
      );
    },
    dateLabel() {
      if (!this.dateFrom) return "";
      const opts = { year: "numeric", month: "short", day: "numeric" };
      const from = new Date(this.dateFrom + "T00:00:00");
      if (this.dateFrom === this.dateTo) {
        return from.toLocaleDateString("en-US", opts);
      }
      const to = new Date(this.dateTo + "T00:00:00");
      return (
        from.toLocaleDateString("en-US", opts) +
        " – " +
        to.toLocaleDateString("en-US", opts)
      );
    },
    totals() {
      const t = this.perUserStats.reduce(
        (acc, u) => {
          acc.total += u.total || 0;
          acc.completed += u.completed || 0;
          acc.missed += u.missed || 0;
          return acc;
        },
        { total: 0, completed: 0, missed: 0 }
      );
      t.rate =
        t.total > 0 ? Math.round((t.completed / t.total) * 100 * 10) / 10 : 0;
      return t;
    },
  },
  methods: {
    openDialog(metric) {
      this.activeMetric = metric;
      this.selectedUser = null;
      this.userTaskLogs = [];
      this.showDialog = true;
    },
    onViewChange(type) {
      this.$emit("view-change", type);
    },
    async selectUser(user) {
      this.selectedUser = user;
      this.loadingUserTasks = true;
      this.userTaskLogs = [];
      try {
        const params = {
          user_id: user.user_id,
          date_from: this.dateFrom,
          date_to: this.dateTo,
        };
        const response = await axios.get("/task_logs", { params });
        this.userTaskLogs = response.data || [];
      } catch (e) {
        this.userTaskLogs = [];
      }
      this.loadingUserTasks = false;
    },
    taskDate(log) {
      // For one-time tasks, use the task's specific_date; otherwise use log_date
      if (
        log.task &&
        log.task.recurrence_type === "none" &&
        log.task.specific_date
      ) {
        return log.task.specific_date;
      }
      return log.log_date;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      // Handle both "2026-02-22" and "2026-02-22T00:00:00.000000Z" formats
      const raw = String(dateStr).substring(0, 10);
      const d = new Date(raw + "T00:00:00");
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    statusColor(status) {
      const map = {
        completed: "green",
        acknowledged: "teal",
        missed: "red",
        ongoing: "orange",
        pending: "grey",
      };
      return map[status] || "grey";
    },
  },
};
</script>

<style scoped>
.stat-card {
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
}
.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}
.highlight-col {
  background: rgba(25, 118, 210, 0.06);
}
.user-row {
  cursor: pointer;
  transition: background 0.15s;
}
.user-row:hover {
  background: rgba(25, 118, 210, 0.08) !important;
}
</style>
